import type { ErrorInfo } from 'react'

import { logError } from 'lib/tracking/datadog'
import { reloadOnceForStaleDeploy } from 'lib/tracking/helpers/reloadOnceForStaleDeploy/reloadOnceForStaleDeploy'
import { isChunkLoadError } from 'lib/tracking/helpers/isChunkLoadError/isChunkLoadError'

interface RecoverFromChunkLoadErrorParams {
  error: unknown
  info?: ErrorInfo
}

const CHUNK_RELOAD_GUARD_KEY = 'chunk-reload-attempted-at'

const toErrorShape = (error: unknown): { name?: string; message?: string } =>
  error instanceof Error ? { name: error.name, message: error.message } : { message: String(error) }

/**
 * Recovers from a stale-deploy chunk miss (a dynamic `import()` for a content-hashed bundle that a
 * new deploy rotated off the CDN) by reloading the page once so the browser re-fetches the fresh
 * chunk manifest. Returns whether the error was a `ChunkLoadError` this helper took ownership
 * of — when `true`, the caller must NOT also `logError`, because either a reload is in flight or
 * this helper already logged it.
 *
 * When a reload can't safely fire (SSR, unavailable storage, a repeat within the guard window, or an
 * offline tab) the error is `logError`d instead so the user lands on the normal error fallback rather
 * than looping. See `reloadOnceForStaleDeploy` for the guard state machine.
 */
export const recoverFromChunkLoadError = ({ error, info }: RecoverFromChunkLoadErrorParams): boolean => {
  if (!isChunkLoadError(toErrorShape(error))) {
    return false
  }

  reloadOnceForStaleDeploy({
    guardKey: CHUNK_RELOAD_GUARD_KEY,
    action: 'chunk_load_error',
    onSkipReload: () => logError(error, { componentStack: info?.componentStack }),
  })

  return true
}
